import { createAsyncThunk } from '@reduxjs/toolkit';

import personsServices, { USER_WITHOUT_OID } from 'services/persons';
import { notify } from 'components/notification/notificationSlice';
import { getRedirectTenantLink, isSmartHubDomain } from 'utils/domainService';
import { LoginFormValues } from 'entities/login';
import { getTenant } from 'theme/selectors';
import { RootState } from 'state/store';
import path from 'common/path';
import {
  setBusinessesId,
  setOtpSession,
  setUserEmail,
  logOut,
} from 'utils/authService';

import { getLoginData } from './selectors';

export const fetchBusinessesByPerson = createAsyncThunk(
  'login/fetchBusinessesByEmail',
  async ({ person, navigate }: any) => {
    try {
      const businesses = await personsServices.getPersonBusinesses(
        person.personsId,
      );

      if (businesses.length === 0) {
        // a signed-in person with no business yet goes straight to the business form
        navigate(
          person.emailAddress
            ? {
                pathname: path.profile,
                search: '?email=' + person.emailAddress,
              }
            : path.profile,
        );
        return null;
      }

      if (businesses.length === 1) {
        const redirectLink = getRedirectTenantLink(businesses[0]);

        if (redirectLink) {
          logOut();
          window.location.href = redirectLink;
          return null;
        }

        setBusinessesId(businesses[0].businessesId);
        navigate(path.dashboard);
        return null;
      }

      return businesses;
    } catch (e) {
      return Promise.reject(e);
    }
  },
);

// why the email check (VerifyEmail) failed: the status, and whether the shared interceptor already toasted it
export interface VerifyPersonFailure {
  status?: number;
  reported: boolean;
}

// the check was rejected (429 from the rate limiter, a network error) or came back empty (ErrorFound): say so
export const notifyVerifyPersonFailed = (dispatch: any, result: any) => {
  const failure: VerifyPersonFailure | undefined = result?.payload;

  if (failure?.reported) {
    return;
  }

  dispatch(
    notify({
      variant: 'error',
      message:
        failure?.status === 429
          ? 'login:requestFailed.tooManyAttempts'
          : 'login:requestFailed.generic',
      duration: 7000,
    }),
  );
};

export const verifyPerson: any = createAsyncThunk(
  'login/verifyPerson',
  async (data: LoginFormValues, thunkAPI) => {
    if (data.email === undefined) {
      return Promise.reject('Email is required');
    }
    try {
      let result: any = null;

      const tenant = getTenant(thunkAPI.getState() as RootState);

      result = await personsServices.verifyEmail({
        emailAddress: data.email,
        // a smart-hub.com page can't keep the session cookie, so never send a code from there (see getOtpLoginUrl)
        sendCode: (data.sendCode && !isSmartHubDomain()) || false,
        tenantId: tenant.tenantsId,
      });

      if (result === USER_WITHOUT_OID && data.sendCode && !isSmartHubDomain()) {
        return 'sendCode';
      }

      return result;
    } catch (e: any) {
      const failure: VerifyPersonFailure = {
        status: e?.response?.status,
        reported:
          e?.response?.status === 400 &&
          Array.isArray(e?.response?.data?.ErrorMessages) &&
          e.response.data.ErrorMessages.length > 0,
      };

      return thunkAPI.rejectWithValue(failure);
    }
  },
);

export const resendCode: any = createAsyncThunk(
  'login/resendCode',
  async (_: void, thunkApi) => {
    try {
      const data = getLoginData(thunkApi.getState() as RootState);

      await thunkApi.dispatch(verifyPerson(data));

      thunkApi.dispatch(
        notify({
          message: 'login:code.resendCodeSuccess',
          variant: 'success',
          duration: 7000,
        }),
      );
    } catch (e) {
      return Promise.reject(e);
    }
  },
);

// Services' PersonsService.AzureLoginRequiredMessage
const MICROSOFT_LOGIN_REQUIRED = 'Please sign in with Microsoft';

export const verifyCode: any = createAsyncThunk(
  'login/verifyCode',
  async ({ verificationCode, navigate }: any, thunkApi) => {
    try {
      const { email } = getLoginData(thunkApi.getState() as RootState);

      const result: any = await personsServices.verifyUserEnteredEmail({
        emailAddress: email || '',
        verificationCode,
      });

      // account has been locked due to multiple unsuccessful login attempts
      if (result === 0) {
        thunkApi.dispatch(
          notify({
            variant: 'error',
            message: 'login:emailLockedAlert',
          }),
        );
        return false;
      }

      if (result === true || result?.verificationResult) {
        // the session is an HttpOnly cookie set by the BFF; only remember that it exists
        if (result?.verificationResult) {
          setOtpSession();
          setUserEmail(email || '');
        }

        // code is valid: a brand-new person goes straight to the business form; anyone else to the business
        // list, which sends a person with no business to the same form
        if (result?.isNewPerson === true) {
          navigate({ pathname: path.profile, search: '?email=' + email });
        } else {
          navigate(path.businesses);
        }
      } else {
        // code is invalid, or the account must sign in with Microsoft (Services sends that reason in the result)
        thunkApi.dispatch(
          notify({
            variant: 'error',
            message:
              result?.displayMessage === MICROSOFT_LOGIN_REQUIRED
                ? 'login:code.validation.useMicrosoft'
                : 'login:code.validation.valid',
            duration: 7000,
          }),
        );
      }

      return true;
    } catch (e) {
      return Promise.reject(e);
    }
  },
);
